import React, { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import {
  Business as BusinessIcon,
  CalendarToday as CalendarTodayIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import moment from "moment";
import Teachr from "../../../Objects/models/Teachr";
import { AppDispatch } from "../../../Redux/store";
import { ProfileTeachr } from "../../../Objects/models/TeachrUpdateRCS";
import Teachr_Repository from "../../../Repositories/Teachr_Repository";
import { userTeachrSetAccountAction } from "../../../Redux/Actions/UserActions";
import ValidateSiret from "../../../Validation/Siret";
import Functions from "../../../Helpers/Functions";
import FormInput from "../../../Components/Ui/FormInput";
import { useTailwindBrand } from "../../../Components/shared";
import { ENV_TEACHR, ENV_VERIFIED_TRAINER, getActivePrestataireBrandConfig, getActivePrestationBrandConfig, isBrandActive } from "../../../config/environment.config";
import { isCooperativeCoach, VT_SAP_SIREN } from "../../../Utils/VerifiedTrainerAmounts";
import { SHOW_SUCCESS } from "../../../Redux/Reducers/MessageReducers";
import Settings from "../../../Components/Settings";
import SettingsSection from "../../../Components/SettingsSection";

const BusinessStep = ({ teachr, urssafTdae }: any) => {
  const dispatch = useDispatch<AppDispatch>();
  const { brandConfig } = useTailwindBrand();
  const [isEditing, setIsEditing] = useState(!teachr?.businessType || !teachr?.RCS);
  const [load, setLoad] = useState(false);
  const [form, setForm] = useState({
    businessType: teachr?.businessType ?? "",
    siret: teachr?.RCS ?? "",
  });
  const [error, setError] = useState({
    businessType: false,
    siret: false,
  });

  const resetFormFromTeachr = useCallback(() => {
    setForm({
      businessType: teachr?.businessType ?? "",
      siret: teachr?.RCS ?? "",
    });
    setError({ businessType: false, siret: false });
  }, [teachr]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSelectChange = useCallback((event: SelectChangeEvent<string>) => {
    setForm((prev) => ({ ...prev, businessType: event.target.value }));
  }, []);

  const validate = (): boolean => {
    const nextError = {
      businessType: false,
      siret: !ValidateSiret(form.siret).valid,
    };
    setError(nextError);
    return !nextError.siret;
  };

  const updateTeachr = async () => {
    if (!validate()) return;
    setLoad(true);
    const updatedTeachr: Teachr = { ...teachr };
    updatedTeachr.RCS = form.siret;
    updatedTeachr.businessType = form.businessType;
    const body = new ProfileTeachr(
      updatedTeachr.RCS ?? "",
      updatedTeachr.description,
      updatedTeachr?.address?.id,
      updatedTeachr.iban ?? "",
      updatedTeachr?.businessType ?? "",
      updatedTeachr.bic ?? "",
      updatedTeachr.tauxTva ?? "",
      updatedTeachr.tvaNumber ?? ""
    );
    if (!updatedTeachr.id) {
      setLoad(false);
      return;
    }
    try {
      const response = await Teachr_Repository.updateProfilTeachr(updatedTeachr.id, body);
      if (response) {
        updatedTeachr.RCS = response.RCS;
        updatedTeachr.businessType = response.businessType;
        const serializedTeachr = JSON.parse(JSON.stringify(updatedTeachr));
        if (serializedTeachr) {
          dispatch(userTeachrSetAccountAction(serializedTeachr));
          dispatch({ type: "SET_TEACHR_INFO", payload: { teachr: serializedTeachr } });
        }
        dispatch({
          type: SHOW_SUCCESS,
          payload: "Vos informations d'entreprise ont été mises à jour.",
          title: "Modifications enregistrées",
        });
        setIsEditing(false);
      }
    } catch (err) {
      console.warn(err);
    } finally {
      setLoad(false);
    }
  };

  const handleSave = () => updateTeachr();
  const handleCancel = () => {
    resetFormFromTeachr();
    setIsEditing(false);
  };

  const primaryHex = brandConfig.colors?.primary ?? "#1976d2";

  const urssafInfo = [
    {
      label: "Date de création",
      value: urssafTdae?.startActivity ? moment(urssafTdae?.startActivity).format("DD/MM/YYYY") : "",
      icon: <CalendarTodayIcon sx={{ fontSize: 20 }} />,
      show: !!urssafTdae?.startActivity,
    },
    {
      label: "Périodicité de déclaration",
      value: urssafTdae?.periodicity ? (urssafTdae?.periodicity === "M" ? "Mensuel" : "Trimestriel") : "",
      icon: <ScheduleIcon sx={{ fontSize: 20 }} />,
      show: !!urssafTdae?.periodicity,
    },
  ];

  const sapInfo = [
    {
      label: "Type d'activité",
      value: isBrandActive(ENV_TEACHR) ? "Professeur particulier" : "Aide à domicile",
      icon: <PersonIcon sx={{ fontSize: 20 }} />,
    },
    // Prof passé par la coopérative : les cours sont déclarés sous le numéro SAP de Verified Trainer SAP, pas le sien
    isCooperativeCoach(teachr)
      ? {
          label: "Numéro SAP (coopérative Verified Trainer)",
          value: `SAP${VT_SAP_SIREN}`,
          icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
        }
      : {
          label: "Numéro SAP",
          value: teachr?.RCS ? `SAP${teachr.RCS.slice(0, 9)}` : "",
          icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
        },
  ];

  const businessTypeOptions = useMemo(() => {
    return Functions.businessTypeList.map((item) => item.label);
  }, []);

  return (
    <Settings
      title="Informations sur votre auto-entreprise"
      icon={
        <BusinessIcon
          sx={{
            color: brandConfig.colors?.primary ?? "primary.main",
            fontSize: 28,
          }}
        />
      }
    >
      <SettingsSection
        title="Informations sur votre auto-entreprise"
        subtitle="Gestion de vos informations d'entreprise et de service à la personne"
        isEditing={isEditing}
        onEditClick={() => setIsEditing(true)}
        onSave={handleSave}
        onCancel={handleCancel}
        loading={load}
      >
        <Grid item xs={12}>
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.200",
              bgcolor: "grey.50",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <BusinessIcon sx={{ color: primaryHex, fontSize: 24 }} />
              <Typography variant="h6" fontWeight="600" fontFamily={brandConfig.fontFamily} color="text.primary">
                Informations d'entreprise
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="business-type-label" sx={{ fontFamily: brandConfig.fontFamily }}>
                    Forme de la société
                  </InputLabel>
                  <Select
                    labelId="business-type-label"
                    id="business-type"
                    name="businessType"
                    value={form.businessType}
                    onChange={handleSelectChange}
                    label="Forme de la société"
                    sx={{ fontFamily: brandConfig.fontFamily }}
                    readOnly={!isEditing}
                    className={`  border-gray-400 bg-white rounded-md border`}
                  >
                    <MenuItem value="">
                      <em>Ex : Auto entrepreneur</em>
                    </MenuItem>
                    {Functions.businessTypeList.map((item, i) => (
                      <MenuItem key={i} value={item.value} sx={{ fontFamily: brandConfig.fontFamily }}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Numéro du SIRET"
                  name="siret"
                  type="text"
                  value={form.siret}
                  onChange={handleChange}
                  placeholder="Ex : 992XXXXXXX"
                  error={error.siret ? ValidateSiret(form.siret).message : undefined}
                  readOnly={!isEditing}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {urssafInfo.some((info) => info.show) && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 1,
                border: "1px solid",
                borderColor: `${primaryHex}40`,
                bgcolor: `${primaryHex}14`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <InfoIcon sx={{ color: primaryHex, fontSize: 24 }} />
                <Typography variant="h6" fontWeight="600" fontFamily={brandConfig.fontFamily} sx={{ color: primaryHex }}>
                  Informations URSSAF
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {urssafInfo.map(
                  (info, index) =>
                    info.show && (
                      <Grid item xs={12} md={6} key={index}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: 1, bgcolor: "background.paper" }}>
                          <Box sx={{ color: primaryHex }}>{info.icon}</Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                              {info.label}
                            </Typography>
                            <Typography variant="body2" fontWeight="600" color="text.primary">
                              {info.value}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )
                )}
              </Grid>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} className={`${isBrandActive(ENV_VERIFIED_TRAINER) ? 'hidden' : ''}`}>
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.200",
              bgcolor: "grey.50",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PersonIcon sx={{ color: primaryHex, fontSize: 24 }} />
              <Typography variant="h6" fontWeight="600" fontFamily={brandConfig.fontFamily} color="text.primary">
                Service à la personne
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 1,
                bgcolor: "warning.50",
                border: "1px solid",
                borderColor: "warning.100",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <InfoIcon sx={{ color: "warning.main", fontSize: 22, flexShrink: 0, mt: 0.25 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight="600" color="warning.dark" sx={{ mb: 0.5, fontFamily: brandConfig.fontFamily }}>
                    Agrément de service à la personne
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: brandConfig.fontFamily }}>
                    Le numéro d'agrément de service à la personne permet aux {getActivePrestataireBrandConfig("s")} dispensant des {getActivePrestationBrandConfig("s")} à domicile de bénéficier d'une déduction fiscale.
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {sapInfo.map((info, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "success.50",
                      border: "1px solid",
                      borderColor: brandConfig.colors.text,
                    }}
                  >
                    <Box sx={{ color: brandConfig.colors.primary }}>{info.icon}</Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ mb: 0.5, fontFamily: brandConfig.fontFamily }}>
                        {info.label}
                      </Typography>
                      <Typography variant="body2" fontWeight="600" color={brandConfig.colors.primary} sx={{ fontFamily: brandConfig.fontFamily }}>
                        {info.value}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </SettingsSection>
    </Settings>
  );
};

export default BusinessStep;
